import { createElement, useEffect, useRef, useState } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";

const COLORS = ["#13294B", "#45BFB4", "#F59E0B", "#64748B", "#E11D48", "#8B5CF6"];
const GAP = 14, RAD = Math.PI / 180;
const labelText = (d) => `${d.name.replace(/^./, (c) => c.toUpperCase())} · ${d.value}`;

// Leader-line label positions relative to the centre, pushed apart vertically per side so they never overlap.
function layout(data, R, bound) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  let acc = 0;
  const pts = data.map((d, i) => {
    const mid = (acc + d.value / 2) / total * 360;
    acc += d.value;
    const cos = Math.cos(-mid * RAD), sin = Math.sin(-mid * RAD);
    return { i, cos, sin, right: cos >= 0, y: (R + 16) * sin };
  });
  for (const side of [true, false]) {
    const s = pts.filter((p) => p.right === side).sort((a, b) => a.y - b.y);
    s.forEach((p, k) => { if (k) p.y = Math.max(p.y, s[k - 1].y + GAP); });
    const over = s.length ? s[s.length - 1].y - bound : 0;
    if (over > 0) s.forEach((p) => { p.y -= over; });
    s.forEach((p, k) => { if (k) p.y = Math.max(p.y, s[k - 1].y + GAP); if (p.y < -bound) p.y = -bound + k * GAP; });
  }
  return pts;
}

function Label({ cx, cy, index, pts, data, R }) {
  const p = pts[index];
  const dir = p.right ? 1 : -1;
  const ex = cx + dir * (R + 10), ey = cy + p.y;
  return (
    <g data-testid={`chart-label-${data[index].name.replace(/\s+/g, "-")}`}>
      <polyline points={`${cx + R * p.cos},${cy + R * p.sin} ${cx + (R + 6) * p.cos},${cy + (R + 6) * p.sin} ${ex},${ey}`} fill="none" stroke="#94A3B8" strokeWidth={1} />
      {createElement("text", { x: ex + dir * 4, y: ey, dy: 4, textAnchor: p.right ? "start" : "end", fontSize: 11, fill: "#334155" }, labelText(data[index]))}
    </g>
  );
}

function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** Pie/doughnut with outside labels + leader lines; radius and centre adapt to label widths so nothing clips or overlaps. */
export function DonutChart({ data: raw, height = 240, inner = 0.62, testId }) {
  const [ref, w] = useWidth();
  const data = raw.filter((d) => d.value > 0);
  const bound = height / 2 - 12;
  const side = (right) => Math.max(0, ...layout(data, 50, bound).filter((p) => p.right === right).map((p) => labelText(data[p.i]).length * 6.2));
  const [lw, rw] = [side(false), side(true)];
  const R = Math.max(32, Math.min(Math.floor(height / 2 - 30), Math.floor((w - lw - rw - 40) / 2)));
  const cx = Math.round((lw + 18 + R + (w - rw - 18 - R)) / 2);
  const pts = layout(data, R, bound);
  return (
    <div ref={ref} className="w-full" style={{ height }} data-testid={testId}>
      {w > 0 && <PieChart width={w} height={height}>
        <Pie data={data} cx={cx} dataKey="value" nameKey="name" innerRadius={Math.round(R * inner)} outerRadius={R} isAnimationActive={false} labelLine={false} label={(p) => <Label {...p} pts={pts} data={data} R={R} />}>
          {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
        </Pie>
        <Tooltip />
      </PieChart>}
    </div>
  );
}
